import { useFetch } from "@/lib/axios.config.ts";
import { useMutation, useQuery } from "@tanstack/react-query";

export const P2PKeys = {
  Search: "p2p-search",
  Transfer: "p2p-transfer",
  Recent: "p2p-recent-recipients",
  Quote: "p2p-quote",
  FAQs: "p2p-faqs",
} as const;

export interface P2pFAQ {
  id: string;
  question: string;
  answer: string;
  order: number;
}

export interface P2PUser {
  id: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
}

export interface P2PRecentRecipient extends P2PUser {
  last_transferred_at: string;
}

export interface P2PTransferResponse {
  success: boolean;
  message: string;
  transfer_id: string | null;
  transaction_id: string | null;
  status: string | null;
  sender_balance: string | null;
  commission_uzs: string | null;
}

export interface P2PQuoteResponse {
  amount: string;
  commission_uzs: string;
  total_charge_uzs: string;
  don_to_uzs_rate: string;
  commission_percent: string;
  min_amount: string;
  max_amount: string | null;
  valid: boolean;
  error_code: string | null;
  error_message: string | null;
}

export const useSearchUsers = (query: string) => {
  return useQuery({
    queryKey: [P2PKeys.Search, query],
    queryFn: async () => {
      const res = await useFetch.get<P2PUser[]>("wallet/P2PSearchUser", {
        params: { q: query },
      });
      return res.data;
    },
    // Backend requires q >= 3 chars (min_length=3); querying below that just
    // 422s and burns the search rate limit.
    enabled: query.length >= 3,
    staleTime: 5 * 1000,
  });
};

export const useRecentRecipients = (limit: number = 5) => {
  return useQuery({
    queryKey: [P2PKeys.Recent, limit],
    queryFn: async () => {
      const res = await useFetch.get<P2PRecentRecipient[]>(
        "wallet/P2PRecentRecipients",
        { params: { limit } }
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
};

/**
 * Quote the commission for a given DON amount. Server reads P2PSettings.
 * Use this to populate the "Commission (UZS, from card)" line in the UI
 * before the user commits to the transfer.
 */
export const useP2PQuote = (amount: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: [P2PKeys.Quote, amount],
    queryFn: async () => {
      const res = await useFetch.post<P2PQuoteResponse>("wallet/P2PQuote", {
        amount,
      });
      return res.data;
    },
    enabled: enabled && amount > 0,
    staleTime: 60 * 1000,
  });
};

export const useP2PTransfer = () => {
  return useMutation({
    mutationKey: [P2PKeys.Transfer],
    mutationFn: async (data: {
      receiver_username: string;
      amount: number;
      card_id: string;
      // Stable per transfer intent — lets the backend reject a replay
      // (retry / double-tap / retry after a post-charge 500) so the card is
      // never charged twice. Sent as the standard Idempotency-Key header.
      idempotency_key?: string;
    }) => {
      const { idempotency_key, ...body } = data;
      const res = await useFetch.post<P2PTransferResponse>(
        "wallet/P2PTransfer",
        body,
        idempotency_key
          ? { headers: { "Idempotency-Key": idempotency_key } }
          : undefined
      );
      return res.data;
    },
  });
};

export const useGetP2pFAQs = () => {
  return useQuery({
    queryKey: [P2PKeys.FAQs],
    queryFn: async () => {
      const res = await useFetch.get<P2pFAQ[]>("wallet/P2pFAQs");
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};
